/**
 * CompanySignatoryBlock — displays signatories of a single company (PJ participant)
 * with checkbox selection, validity indicators, and digital certificate toggle.
 *
 * AF-2757: Seleção de signatários por empresa.
 */
import { useMemo } from 'react';
import SelectTextField from '../../molecules/SelectTextField';
import TextEdit from '../../molecules/TextEdit';
import type { Signatory } from '../../../services/personApi';
import { resolveSignatoryValidity, checkRepresentationRequirement, getAllowedRolesForSignatory } from './signatorySelection';
import { formatVerificationDate, getVerificationBadgeColor } from './ContractSignPage.helpers';
import MaskEdit from '../../organisms/MaskEdit';
import { formatMobileMask, isPhoneEdited, isValidSignerEmail, isValidSignerMobile } from './signerPayload';

export interface SignatorySelection {
    signatory: Signatory;
    selected: boolean;
    useDigitalCertificate: boolean;
    contractRole: string;
    /** True when this signatory's CPF was found among the signers extracted from the PDF */
    inDocument?: boolean;
    /** Name as written in the PDF, when found */
    pdfName?: string;
    /** True when the PDF name and the Person name don't match */
    nameDiverges?: boolean;
    /** Which name goes into the signature payload when they diverge. Defaults to the Person record. */
    nameSource?: 'person' | 'document';
    /** E-mail editado só para este envio (AF-4138). Ausente = e-mail do cadastro, que não muda. */
    emailOverride?: string;
    /** Celular editado só para este envio (AF-4181). Ausente = celular do cadastro, que não muda. */
    phoneOverride?: string;
}

interface CompanySignatoryBlockProps {
    companyName: string;
    companyCnpj: string;
    participantSource: 'ASSIGNOR' | 'RECIPIENT';
    signatories: Signatory[];
    selections: SignatorySelection[];
    contractValue: number | null;
    allowedProducts: string[];
    availableRoles: { value: string; label: string }[];
    defaultRole: string;
    lastVerifiedAt?: string | null;
    onToggleSelection: (individualUuid: string) => void;
    onToggleDigitalCertificate: (individualUuid: string) => void;
    onRoleChange: (individualUuid: string, role: string) => void;
    onNameSourceChange: (individualUuid: string, source: 'person' | 'document') => void;
    onEmailChange: (individualUuid: string, email: string) => void;
    onPhoneChange: (individualUuid: string, phone: string) => void;
}

export default function CompanySignatoryBlock({
    companyName,
    companyCnpj,
    participantSource,
    signatories,
    selections,
    contractValue,
    allowedProducts,
    availableRoles,
    defaultRole,
    lastVerifiedAt,
    onToggleSelection,
    onToggleDigitalCertificate,
    onRoleChange,
    onNameSourceChange,
    onEmailChange,
    onPhoneChange,
}: CompanySignatoryBlockProps) {
    // Compute validity for each signatory
    const signatoryDetails = useMemo(() => {
        return signatories.map(sig => {
            const sel = selections.find(s => s.signatory.individualUuid === sig.individualUuid);
            const validity = resolveSignatoryValidity(sig, contractValue, allowedProducts);
            return {
                signatory: sig,
                selected: sel?.selected ?? false,
                useDigitalCertificate: sel?.useDigitalCertificate ?? false,
                useDigitalCertificateOriginal: (sig.signAsEntries || []).some(e => e.useDigitalCertificate),
                contractRole: sel?.contractRole ?? defaultRole,
                validity,
                hasEmail: !!sig.email,
                sendEmail: sel?.emailOverride ?? sig.email ?? '',
                sendEmailValid: isValidSignerEmail(sel?.emailOverride ?? sig.email ?? ''),
                emailEdited: sel?.emailOverride !== undefined && sel.emailOverride.trim() !== (sig.email ?? ''),
                sendPhone: sel?.phoneOverride ?? sig.mobile ?? '',
                phoneEdited: sel ? isPhoneEdited(sel) : false,
                sendPhoneValid: isValidSignerMobile(sel?.phoneOverride ?? sig.mobile ?? ''),
                inDocument: !!sel?.inDocument,
                pdfName: sel?.pdfName,
                nameDiverges: !!sel?.nameDiverges,
                nameSource: sel?.nameSource ?? 'person',
                allowedRolesForSigner: getAllowedRolesForSignatory(sig, availableRoles),
            };
        });
    }, [signatories, selections, contractValue, allowedProducts, availableRoles, defaultRole]);

    // Representation requirement check (only for selected signatories)
    const selectedSignatories = signatoryDetails
        .filter(d => d.selected)
        .map(d => d.signatory);
    const representationIssue = checkRepresentationRequirement(selectedSignatories, allowedProducts);

    // Verification badge
    const verificationColor = getVerificationBadgeColor(lastVerifiedAt);
    const verificationDate = formatVerificationDate(lastVerifiedAt);
    const verificationColorClasses = verificationColor === 'red'
        ? 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
        : verificationColor === 'amber'
        ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700'
        : 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:border-slate-600';

    const sourceLabel = participantSource === 'ASSIGNOR' ? 'Cliente' : 'Agrolend';

    return (
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
            {/* Company header */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 flex items-center gap-3 flex-wrap">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{companyName}</h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">{companyCnpj}</span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                    participantSource === 'ASSIGNOR'
                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                }`}>
                    {sourceLabel}
                </span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded border ${verificationColorClasses}`}>
                    {verificationDate ? `Verificação: ${verificationDate}` : 'Verificação não disponível'}
                </span>
            </div>

            {/* Signatories list */}
            <div className="p-4 space-y-2">
                {signatoryDetails.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                        Nenhum signatário cadastrado para esta empresa.
                    </p>
                )}

                {signatoryDetails.map(({ signatory, selected, useDigitalCertificate, useDigitalCertificateOriginal, contractRole, validity, hasEmail, sendEmail, sendEmailValid, emailEdited, sendPhone, phoneEdited, sendPhoneValid, inDocument, pdfName, nameDiverges, nameSource, allowedRolesForSigner }) => (
                    <div
                        key={signatory.individualUuid}
                        className={`p-3 rounded-lg border transition-colors ${
                            !validity.valid
                                ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/10'
                                : selected
                                ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/10'
                                : 'border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-800'
                        }`}
                    >
                        <div className="flex items-center gap-3 flex-wrap">
                            {/* Checkbox — invalid signatories cannot be selected */}
                            <input
                                type="checkbox"
                                checked={selected}
                                disabled={!validity.valid}
                                onChange={() => onToggleSelection(signatory.individualUuid)}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label={`Selecionar ${signatory.fullName}`}
                                title={validity.valid ? undefined : validity.reasons.join('; ')}
                            />

                            {/* Name + CPF */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {signatory.fullName}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {signatory.socialId}
                                    </span>
                                    {signatory.position && (
                                        <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 dark:bg-slate-600 dark:text-gray-300 rounded">
                                            {signatory.position}
                                        </span>
                                    )}
                                    {(signatory.signAsEntries || []).map((e, i) => (
                                        <span key={i} className="px-1.5 py-0.5 text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 rounded">
                                            {e.signAs}
                                        </span>
                                    ))}
                                    {inDocument && (
                                        <span className="px-1.5 py-0.5 text-xs bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 rounded font-medium">
                                            consta no documento
                                        </span>
                                    )}
                                </div>
                                {nameDiverges && pdfName && (
                                    <div className="mt-1.5 p-2 rounded border border-amber-200 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20">
                                        <p className="text-xs text-amber-700 dark:text-amber-300 font-medium mb-1.5">
                                            ⚠️ Nome divergente entre documento e cadastro — escolha qual usar na assinatura:
                                        </p>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <button
                                                type="button"
                                                onClick={() => onNameSourceChange(signatory.individualUuid, 'person')}
                                                className={`px-2 py-1 text-xs rounded border transition-colors ${
                                                    nameSource === 'person'
                                                        ? 'border-green-500 bg-green-100 text-green-800 font-medium dark:border-green-500 dark:bg-green-900/40 dark:text-green-200'
                                                        : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-100 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-300'
                                                }`}
                                            >
                                                {nameSource === 'person' ? '✓ ' : ''}Cadastro: {signatory.fullName}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => onNameSourceChange(signatory.individualUuid, 'document')}
                                                className={`px-2 py-1 text-xs rounded border transition-colors ${
                                                    nameSource === 'document'
                                                        ? 'border-green-500 bg-green-100 text-green-800 font-medium dark:border-green-500 dark:bg-green-900/40 dark:text-green-200'
                                                        : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-100 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-300'
                                                }`}
                                            >
                                                {nameSource === 'document' ? '✓ ' : ''}Documento: {pdfName}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {selected && (
                                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-4 max-w-2xl">
                                        <div className="flex-1 min-w-[14rem]">
                                            <TextEdit
                                                id={`send-email-${signatory.individualUuid}`}
                                                name={`send-email-${signatory.individualUuid}`}
                                                type="email"
                                                label="E-mail para este envio"
                                                value={sendEmail}
                                                onChange={e => onEmailChange(signatory.individualUuid, e.target.value)}
                                                errorMessage={sendEmailValid ? undefined : 'Informe um e-mail válido'}
                                                helperText={emailEdited ? 'Alterado só neste envio — o cadastro não muda' : undefined}
                                            />
                                        </div>
                                        <div className="w-48">
                                            <MaskEdit
                                                id={`send-phone-${signatory.individualUuid}`}
                                                name={`send-phone-${signatory.individualUuid}`}
                                                type="tel"
                                                label="Celular para este envio"
                                                mask="(__) _____-____"
                                                replacement={{ _: /\d/ }}
                                                value={formatMobileMask(sendPhone)}
                                                onChange={e => onPhoneChange(signatory.individualUuid, e.target.value)}
                                                errorMessage={!sendPhoneValid && phoneEdited ? 'Informe um celular com DDD e 9 dígitos' : undefined}
                                            />
                                            {phoneHelperText(sendPhoneValid, phoneEdited) && (
                                                <p className={`mt-1 text-xs ${sendPhoneValid ? 'text-gray-500 dark:text-gray-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                                    {phoneHelperText(sendPhoneValid, phoneEdited)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {!selected && hasEmail && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">📧 {signatory.email}</p>
                                )}
                                {!selected && !hasEmail && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                                        ⚠️ Sem email — seleção bloqueia o envio
                                    </p>
                                )}
                            </div>

                            {/* Role dropdown — filtered by the signatory's signAs profile */}
                            {allowedRolesForSigner.length > 0 && (
                                <div className="w-52 shrink-0">
                                    <SelectTextField
                                        id={`contract-role-${signatory.individualUuid}`}
                                        name={`contract-role-${signatory.individualUuid}`}
                                        label=""
                                        value={contractRole}
                                        onChange={e => onRoleChange(signatory.individualUuid, e.target.value)}
                                        items={allowedRolesForSigner}
                                        emptyLabel="Selecione o papel..."
                                    />
                                </div>
                            )}
                        </div>

                        {/* Digital certificate toggle */}
                        <div className="mt-2 ml-7 flex items-center gap-2">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={useDigitalCertificate}
                                    onChange={() => onToggleDigitalCertificate(signatory.individualUuid)}
                                    className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span className="text-xs text-gray-600 dark:text-gray-400">Certificado Digital (ICP-Brasil)</span>
                            </label>
                            {useDigitalCertificate && useDigitalCertificateOriginal && (
                                <span className="px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded text-xs font-medium">
                                    cadastrado com certificado
                                </span>
                            )}
                            {useDigitalCertificate !== useDigitalCertificateOriginal && (
                                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 rounded text-xs">
                                    {useDigitalCertificate ? 'ativado manualmente' : 'desativado manualmente'}
                                </span>
                            )}
                        </div>

                        {/* Validity reasons */}
                        {!validity.valid && (
                            <div className="mt-2 ml-7 space-y-0.5">
                                {validity.reasons.map((reason, i) => (
                                    <p key={i} className="text-xs text-red-600 dark:text-red-400">❌ {reason}</p>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Footer: representation requirement */}
            {representationIssue && (
                <div className="px-4 py-2 border-t border-gray-200 dark:border-slate-700 bg-red-50 dark:bg-red-900/10 rounded-b-lg">
                    <p className="text-xs font-medium text-red-600 dark:text-red-400">
                        ⚠️ {representationIssue}
                    </p>
                </div>
            )}

            {selectedSignatories.length > 0 && !representationIssue && (
                <div className="px-4 py-2 border-t border-gray-200 dark:border-slate-700 bg-green-50 dark:bg-green-900/10 rounded-b-lg">
                    <p className="text-xs font-medium text-green-600 dark:text-green-400">
                        ✅ {selectedSignatories.length} signatário(s) selecionado(s)
                    </p>
                </div>
            )}
        </div>
    );
}

function phoneHelperText(valid: boolean, edited: boolean): string | undefined {
    if (!valid && !edited) {
        return 'Celular do cadastro inválido: recebe só por e-mail.';
    }
    if (edited && valid) {
        return 'Alterado só neste envio — o cadastro não muda';
    }
    return undefined;
}
